"use client";
import { SupportedLanguages } from "@ea/shared_types/next/ea.enums";

import { TranslationWrapper } from "@ea/ui/translations/TranslationContext";
import { TranslateFunctionWithNamespace } from "@ea/ui/translations/types";
import { usePlatformsTranslations } from "@ea/ui/translations/useTranslations";
import merge from "lodash/merge";
import {
  AbstractIntlMessages,
  NextIntlClientProvider,
  useLocale,
  useTranslations as useNextIntlTranslations,
} from "next-intl";
import { ReactNode } from "react";
// NextIntlAdapter integrates next-intl with our custom TranslationWrapper.
// This allows our UI components to be integrated into applications with different translation setups.
export const NextIntlAdapter = ({
  children,
  messages,
  locale = SupportedLanguages.EN,
}: {
  children: ReactNode;
  messages: AbstractIntlMessages;
  locale: string;
}) => {
  const { translations: platformTranslations } = usePlatformsTranslations();

  const combinedMessages = {};
  merge(combinedMessages, messages);
  merge(combinedMessages, platformTranslations[locale.toUpperCase()]);

  //TODO: passing translations can be optimized in future, check docs for that
  return (
    <NextIntlClientProvider locale={locale.toLowerCase()} messages={combinedMessages}>
      <TranslationComponent>{children}</TranslationComponent>
    </NextIntlClientProvider>
  );
};

const TranslationComponent = ({ children }: { children: ReactNode }) => {
  const locale = useLocale().toUpperCase();
  const nextIntlT = useNextIntlTranslations();

  const translate: TranslateFunctionWithNamespace = (namespace) => {
    return (key, params) => {
      const fullKey = namespace ? `${namespace}.${String(key)}` : String(key);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return nextIntlT(fullKey as any, params as Record<string, string>);
    };
  };

  return (
    <TranslationWrapper
      translationContext={{
        locale,
        tWithNamespace: translate,
        changeLanguage: () => Promise.resolve(),
      }}
    >
      {children}
    </TranslationWrapper>
  );
};
